'use client';

import {
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import Image from 'next/image';

import type { Step } from '@/components/wizard/Step';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { ApplicationsLogos } from '@/data/inventory/data';

import type { ConnectSaasWorkspaceState } from './connectSaasWorkspaceWizard';

const saasProviders = ['google-workspace', 'microsoft-365'] as const;
type SaasProvider = (typeof saasProviders)[number];

function isSaasProvider(value: string): value is SaasProvider {
  return saasProviders.some((p) => p === value);
}

const providers = [
  {
    value: 'google-workspace' satisfies SaasProvider,
    label: 'Google Workspace',
    logo: ApplicationsLogos.GoogleWorkspace,
  },
  {
    value: 'microsoft-365' satisfies SaasProvider,
    label: 'Microsoft 365',
    logo: ApplicationsLogos.Microsoft365,
  },
];

function ConnectSaasWorkspaceForm(props: StepProps<ConnectSaasWorkspaceState>) {
  const { wizardState, setWizardState } = props;
  const selectedProvider = providers.find(
    (p) => p.value === wizardState.provider
  );

  return (
    <StepContainer
      onBackClick={props.back}
      onNextClick={async () => {
        await props.finish(
          `Workspace "${wizardState.domain}" connected successfully.`
        );
      }}
      canGoNext={() => !!wizardState.domain.trim()}
      nextButtonText='Connect'
      hideStepper
    >
      <CardContent>
        <Stack gap='24px' sx={{ maxWidth: '480px' }}>
          <Typography variant='body1' color='text.secondary'>
            Select a SaaS provider and enter the domain name for the workspace
            you want to connect.
          </Typography>

          <FormControl
            fullWidth
            sx={{ '& .MuiInputBase-root': { height: '40px' } }}
          >
            <InputLabel id='provider-label'>Workspace provider</InputLabel>
            <Select
              labelId='provider-label'
              label='Workspace provider'
              value={wizardState.provider}
              onChange={(e) => {
                const value = e.target.value;
                if (isSaasProvider(value)) {
                  setWizardState((s) => ({ ...s, provider: value }));
                }
              }}
            >
              {providers.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  <Stack direction='row' alignItems='center' gap='8px'>
                    <Image alt={p.label} height={20} width={20} src={p.logo} />
                    <Typography variant='body2'>{p.label}</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack gap='4px'>
            <Typography variant='subtitle2'>Domain name</Typography>
            <input
              type='text'
              placeholder='e.g. acme.com'
              value={wizardState.domain}
              onChange={(e) =>
                setWizardState((s) => ({ ...s, domain: e.target.value }))
              }
              style={{
                padding: '8px 12px',
                border: '1px solid var(--mui-palette-divider)',
                borderRadius: '4px',
                fontSize: '14px',
                background: 'transparent',
                color: 'inherit',
              }}
            />
            {selectedProvider && wizardState.domain && (
              <Typography variant='caption' color='text.secondary'>
                Eon will use a service account (eon-backup@
                {wizardState.domain}) to access this {selectedProvider.label}{' '}
                workspace.
              </Typography>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </StepContainer>
  );
}

export const connectSaasWorkspaceStep: Step<ConnectSaasWorkspaceState> = {
  id: 'connect_saas_workspace',
  Component: ConnectSaasWorkspaceForm,
  nonReturnable: false,
};
